/**
 * configureFilterValueOptions
 * @description Accepts table and metadata and returns sets of filter options
 * @param {Object} data 
 * @param {*} data.tableData Array of tableData (same as what is returned from cube result tablePivot())
 * @param {*} data.tableSlug A slug for this table
 * @param {*} data.metaData MetaData for this table
 *
 * @returns {filterValueOptions: Object} An array of unique valueOptions for each column, indexed by column name. valueOption = {value, name (display name), stringValue}
*/
export default async function configureFilterValueOptions({tableData, tableSlug, metaData}) {
    return new Promise((resolve, reject) => {
        //if there is no tableData, reject
        if (!tableData) reject('No table data');

        const filterValueSets = {}; //for tracking unique values as sets
        const filterValueOptions = {}; //final return object

        //iterate through each row
        tableData.forEach(row => {

            //iterate through each column in the table
            metaData.forEach(column => {

                //if the column doesn't exist as a key in the options, add it
                if (!filterValueSets[column.name]) {
                    filterValueSets[column.name] = new Set();
                }

                //check if the item is not already in the set
                if (!filterValueSets[column.name].has(row[column.name])) {

                    //if so, add it
                    filterValueSets[column.name].add(row[column.name]);

                    //add a key for this column in filterValueOptions if it's not already there
                    if (!filterValueOptions[column.name]) {
                        filterValueOptions[column.name] = [];
                    }
                    
                    //add an object containing the actual value, display value (name), and stringValue (i.e. true => 'true') to the filterValueOptions
                    filterValueOptions[column.name].push({
                        value: row[column.name],
                        name: getFilterValueDisplayName(row[column.name], column),
                        stringValue: getFilterOptionStringValue(row[column.name], column),
                    });
                }
            });
        })
        
        resolve(filterValueOptions);
    })
}

function getFilterValueDisplayName(value, columnMetaData = {}) {
    switch (columnMetaData.type) {
        case "time": {
            const date = new Date(value);

            //break if the date is invalid
            if (isNaN(date.getTime())) {
                break;
            }

            return date.toLocaleDateString("en-US");
        }
        case "boolean": {
            if (value === true) {
                return "Yes";
            } else if (value === false) {
                return "No";
            }
            break;
        }
        default: {
            break;
        }
    }

    if (value === null || value === "") {
        return "Blank";
    }

    return value;
}

//table data is converted to strings for display in the grid. To filter it, you need the string value
function getFilterOptionStringValue(value, columnMetaData = {}) {
    if (value === null) {
        return "";
    }

    switch (columnMetaData.type) {
        case "time": {
            const date = new Date(value);

            //break if the date is invalid
            if (isNaN(date.getTime())) {
                break;
            }

            return date.toLocaleDateString("en-US");
        }
        case "boolean": {
            if (value === true) {
                return "true";
            } else if (value === false) {
                return "false";
            }
            break;
        }
        case "number": {
            return value.toString();
        }
        default: {
            break;
        }
    }

    return value;
}